import { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { useDarkModeContext, useAuthContext, useToast } from "../contexts";
import { usePasswordChecker } from "../api/auth/AzryAuth";
import { AuthInfo } from "../types";
import { storage } from "../utils/storage";
import DarkModeSwitch from "../components/DarkModeSwitch";
import LeftSidePattern_Light from '../images/LeftSidePattern_Light.png';
import LeftSidePattern_Dark from '../images/LeftSidePattern_Dark.png';

export default function Login() {
  const { darkMode } = useDarkModeContext();
  const { setAuthInfo } = useAuthContext();
  const { showError } = useToast();
  const navigate = useNavigate();
  const { isLoading, isSuccess, isError, data: response, error, mutate } = usePasswordChecker();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!username.trim()) newErrors.username = 'Required field';
    if (!password.trim()) newErrors.password = 'Required field';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) {
      mutate({ username, password });
    }
  };

  useEffect(() => {
    if (isError) {
      const status = (error as AxiosError)?.response?.status;
      if (status === 403) {
        showError('Login Failed', 'Invalid username or password');
      } else {
        showError('Connection Error', 'An error occurred during login. Please check your connection.');
      }
    }
  }, [isError, error, showError]);

  useEffect(() => {
    if (response?.data?.user?.enabled === false) {
      showError('Login Failed', 'User is not activated');
    }
  }, [response?.data?.user?.enabled, showError]);

  useEffect(() => {
    if (isSuccess) {
      const user = response?.data?.user;
      if (user?.enabled !== false) {
        const authInfo: AuthInfo = {
          token: response?.data?.token,
          username: user?.username,
          userType: user?.userType,
          organization: user?.organisation,
        };
        storage.setObject('portal_rs_auth', authInfo);
        setAuthInfo(authInfo);
        navigate("/", { replace: true });
      }
    }
  }, [isSuccess, response?.data, setAuthInfo, navigate]);

  return (
    <div style={{
      display: 'flex',
      width: '100%',
      height: '100vh',
      background: darkMode ? '#1A1A1A' : 'transparent'
    }}>
      {/* Top right controls */}
      <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 100 }}>
        <DarkModeSwitch />
      </div>

      {/* Logo */}
      <img
        src={darkMode ? LeftSidePattern_Dark : LeftSidePattern_Light}
        alt=""
        style={{ height: '100vh' }}
      />

      {/* Login form */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flexGrow: 1,
        gap: 20
      }}>
        <h1 style={{
          fontFamily: "'BPG Nino Mtavruli Bold', sans-serif",
          fontSize: 25,
          textAlign: 'center',
          width: 500
        }}>
          Fiscal Device Management Portal
        </h1>

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <div className="flex flex-column gap-2">
            <InputText
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className={errors.username ? 'p-invalid' : ''}
              style={{ width: 250 }}
            />
            {errors.username && <small className="p-error">{errors.username}</small>}
          </div>

          <div className="flex flex-column gap-2">
            <Password
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              feedback={false}
              toggleMask
              className={errors.password ? 'p-invalid' : ''}
              inputStyle={{ width: 250 }}
            />
            {errors.password && <small className="p-error">{errors.password}</small>}
          </div>

          <Button
            type="submit"
            label="Login"
            icon="pi pi-sign-in"
            loading={isLoading}
            disabled={isLoading || isSuccess}
            style={{ width: 250 }}
          />
        </form>
      </div>
    </div>
  );
}
